import { Link } from "react-router-dom"
import { Phone, Mail, Clock, Send, MapPin, Calendar } from "lucide-react"
import { Button } from "../components/ui/button"
import { Card, CardContent } from "../components/ui/card"
import { Badge } from "../components/ui/badge"
import SEO from "./SEO"

export default function Contact() {
  const contactSchema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "name": "Pranaam Hospitals",
    "url": "https://pranaam.pranaamhospitals.in/",
    "logo": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/logo.png",
    "description": "Pranaam Hospitals is a NABH-accredited multi-specialty hospital in Hyderabad offering expert doctors, advanced diagnostics, and 24/7 emergency care.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bachupally",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "addressCountry": "IN"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "contactType": "emergency",
        "telephone": "+91-98485-98285",
        "availableLanguage": ["English", "Telugu", "Hindi"]
      },
      {
        "@type": "ContactPoint",
        "contactType": "appointments",
        "telephone": "+91-99660-60000",
        "availableLanguage": ["English", "Telugu", "Hindi"]
      }
    ],
    "sameAs": [
      "https://www.facebook.com/pranaamhospitals",
      "https://www.instagram.com/pranaamhospitals",
      "https://www.linkedin.com/company/pranaamhospitals"
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "Article",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pranaam.pranaamhospitals.in/contact"
    },
    "headline": "Contact Pranaam Hospitals",
    "description": "Get in touch with Pranaam Hospitals for doctor appointments, emergency medical care, and healthcare enquiries in Hyderabad.",
    "author": {
      "@type": "Organization",
      "name": "Pranaam Hospitals"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Pranaam Hospitals",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/logo.png"
      }
    },
    "datePublished": "2015-01-01",
    "dateModified": "2025-04-01",
    "keywords": [
      "contact Pranaam Hospitals",
      "hospital contact Hyderabad",
      "emergency hospital Hyderabad",
      "book appointment Pranaam Hospitals"
    ]
  }
];

  return (
    <>
    <SEO
  title="Contact Pranaam Hospitals | Book Appointment & Emergency Care Hyderabad"
  description="Contact Pranaam Hospitals for appointments, emergency services, and healthcare enquiries. Reach our multi-specialty hospital in Hyderabad for expert medical care."
  canonical="https://pranaam.pranaamhospitals.in/contact"
  robots="index, follow"
  author="Pranaam Hospitals"
  publisher="Pranaam Hospitals"
  keywords="Contact Pranaam Hospitals, hospital contact number Hyderabad,
            emergency hospital Hyderabad, book doctor appointment Hyderabad,
            Pranaam Hospitals address, multi specialty hospital Hyderabad"
  schema={contactSchema}
/>

    <main className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary/90 to-primary py-16 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto ">
            <Badge className="mb-4 bg-white/20 text-white border-white/30">
              We're Here to Help
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">📞 Contact Us</h1>
            <p className="text-lg opacity-90 mb-4">We're Here to Help You!</p>
            <p className="opacity-90">
              Whether you need medical assistance, have an inquiry, or simply want to give feedback, we're just a call
              or click away. At Pranaam Hospitals, we are committed to providing responsive, compassionate, and expert
              healthcare support.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information and Form */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold mb-6 text-primary">📧 General Inquiries</h2>
                <div className="space-y-4">
                  <Card className="transition-transform hover:translate-y-[-4px]">
                    <CardContent className="flex items-start p-4">
                      <div className="bg-primary/10 p-3 rounded-full mr-4">
                        <Phone className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">Phone</h3>
                        <p className="text-gray-600 mt-1">+91 99660 60000</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="transition-transform hover:translate-y-[-4px]">
                    <CardContent className="flex items-start p-4">
                      <div className="bg-primary/10 p-3 rounded-full mr-4">
                        <Mail className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">Email</h3>
                        <p className="text-gray-600 mt-1">info@pranaamhospitals.in</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="transition-transform hover:translate-y-[-4px]">
                    <CardContent className="flex items-start p-4">
                      <div className="bg-primary/10 p-3 rounded-full mr-4">
                        <Clock className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">Working Hours</h3>
                        <p className="text-gray-600 mt-1">24x7 Emergency Services</p>
                        <p className="text-gray-600">Outpatient Timings: 9:00 AM – 9:00 PM</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="transition-transform hover:translate-y-[-4px]">
                    <CardContent className="flex items-start p-4">
                      <div className="bg-primary/10 p-3 rounded-full mr-4">
                        <Calendar className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">Appointments</h3>
                        <p className="text-gray-600 mt-1">040 230 42777 / 3777 / 35094777</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold mb-6 text-primary">🏥 Hospital Locations</h2>
                <p className="text-gray-600 mb-6">
                  Visit one of our conveniently located hospitals across Hyderabad and surrounding areas.
                </p>
                <Button asChild>
                  <Link to="/locations">
                    <MapPin className="w-4 h-4 mr-2" />
                    View All Locations
                  </Link>
                </Button>
              </div>
            </div>

            {/* Contact Form */}
            <Card className="shadow-lg">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-6 text-primary">📬 Send Us a Message</h2>
                <p className="text-gray-600 mb-6">
                  If you have a specific question, fill out the form below and our team will get back to you shortly.
                </p>
                <form className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                        placeholder="Your name"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                        placeholder="Your phone number"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                        placeholder="Your email"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                        Subject
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                        required
                      >
                        <option value="">Select a subject</option>
                        <option value="appointment">Appointment Inquiry</option>
                        <option value="feedback">Feedback</option>
                        <option value="billing">Billing Question</option>
                        <option value="general">General Inquiry</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                        placeholder="Your message"
                        required
                      ></textarea>
                    </div>
                  </div>

                  <Button type="submit" className="w-full">
                    <Send className="h-4 w-4 mr-2" />
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Branch Locations */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold mb-8 text-center">Our Branches</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Madinaguda Branch */}
            <Card className="overflow-hidden transition-transform hover:translate-y-[-4px]">
              <div className="h-48 overflow-hidden">
                <img
                  src="/images/madinaguda-branch.jpeg"
                  alt="Madinaguda Branch"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-bold text-lg mb-2 flex items-center">
                  {/* <MapPin className="w-4 h-4 mr-1 text-primary" /> */}
                  📍Madinaguda
                </h3>
                <p className="text-gray-600 text-sm mb-3">Opp. Miyapur Bus Depot, Madinaguda, Hyderabad – 500049</p>
                <div className="space-y-2">
                  <a
                    href="tel:+917799828833"
                    className="text-primary hover:text-primary/80 font-medium text-sm flex items-center gap-1"
                  >
                    <Phone className="h-4 w-4" /> +91 77998 28833
                  </a>
                  <a
                    href="mailto:madinaguda@pranaamhospitals.in"
                    className="text-primary hover:text-primary/80 font-medium text-sm flex items-center gap-1"
                  >
                    <Mail className="h-4 w-4" /> madinaguda@pranaamhospitals.in
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Nallagandla Branch */}
            <Card className="overflow-hidden transition-transform hover:translate-y-[-4px]">
              <div className="h-48 overflow-hidden">
                <img
                  src="/images/nallagandla-branch.png"
                  alt="Nallagandla Branch"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-bold text-lg mb-2 flex items-center">📍 Nallagandla</h3>
                <p className="text-gray-600 text-sm mb-3">HUDA Layout, Nallagandla, Hyderabad – 500019</p>
                <div className="space-y-2">
                  <a
                    href="tel:+917799828855"
                    className="text-primary hover:text-primary/80 font-medium text-sm flex items-center gap-1"
                  >
                    <Phone className="h-4 w-4" /> +91 77998 28855
                  </a>
                  <a
                    href="mailto:nallagandla@pranaamhospitals.in"
                    className="text-primary hover:text-primary/80 font-medium text-sm flex items-center gap-1"
                  >
                    <Mail className="h-4 w-4" /> nallagandla@pranaamhospitals.in
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Isnapur Branch */}
            <Card className="overflow-hidden transition-transform hover:translate-y-[-4px]">
              <div className="h-48 overflow-hidden">
                <img
                  src="/images/isnapur-branch.png"
                  alt="Isnapur Branch"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-bold text-lg mb-2 flex items-center">📍Isnapur</h3>
                <p className="text-gray-600 text-sm mb-3">Beside Metro Wine Shop, Isnapur, Hyderabad – 502307</p>
                <div className="space-y-2">
                  <a
                    href="tel:+917799828866"
                    className="text-primary hover:text-primary/80 font-medium text-sm flex items-center gap-1"
                  >
                    <Phone className="h-4 w-4" /> +91 77998 28866
                  </a>
                  <a
                    href="mailto:isnapur@pranaamhospitals.in"
                    className="text-primary hover:text-primary/80 font-medium text-sm flex items-center gap-1"
                  >
                    <Mail className="h-4 w-4" /> isnapur@pranaamhospitals.in
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Zaheerabad Branch */}
            <Card className="overflow-hidden transition-transform hover:translate-y-[-4px]">
              <div className="h-48 overflow-hidden">
                <img
                  src="/images/zaheerabad-branch.png"
                  alt="Zaheerabad Branch"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4">
                <h3 className="font-bold text-lg mb-2 flex items-center">📍Zaheerabad</h3>
                <p className="text-gray-600 text-sm mb-3">Platinum Pride Complex, Mogdampally, Zaheerabad – 502220</p>
                <div className="space-y-2">
                  <a
                    href="tel:+917799828844"
                    className="text-primary hover:text-primary/80 font-medium text-sm flex items-center gap-1"
                  >
                    <Phone className="h-4 w-4" /> +91 77998 28844
                  </a>
                  <a
                    href="mailto:zaheerabad@pranaamhospitals.in"
                    className="text-primary hover:text-primary/80 font-medium text-sm flex items-center gap-1"
                  >
                    <Mail className="h-4 w-4" /> zaheerabad@pranaamhospitals.in
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      
      {/* Map Section */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold mb-8 text-center">Find Us on the Map</h2>
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="aspect-video w-full">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3805.4034034234234!2d78.3456789!3d17.4567890!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTfCsDI3JzI0LjQiTiA3OMKwMjAnNDQuNiJF!5e0!3m2!1sen!2sin!4v1620000000000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen={true}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Pranaam Hospitals Location"
                className="w-full h-full"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold mb-8 text-center">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            <Card>
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-2">How do I schedule an appointment?</h3>
                <p className="text-gray-600">
                  You can schedule an appointment by calling our appointment desk at +91 99660 60000, using our online
                  appointment form, or visiting any of our branches in person.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-2">What insurance plans do you accept?</h3>
                <p className="text-gray-600">
                  We accept most major insurance plans. Please contact our billing department for specific information
                  about your insurance coverage.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-2">Do you offer emergency services?</h3>
                <p className="text-gray-600">
                  Yes, our emergency department is open 24/7 at all our branches to handle medical emergencies.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-2">How can I access my medical records?</h3>
                <p className="text-gray-600">
                  You can request your medical records by filling out a form at our reception desk or by sending an email
                  to info@pranaamhospitals.in with your details.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </main>
  </>
  )
}



